import { type ReactNode, type ReactElement, type Ref } from 'react';
import cn from 'classnames';
import Flex from '@creditflowresearch/uilib/Flex';
import Grid, { type TResponsiveValue } from '@creditflowresearch/uilib/Grid';

import styles from './tile.module.scss';

interface ITileBaseProps {
  dataTest?: string,
  children?: ReactNode,
  className?: string,
  ref?: Ref<HTMLDivElement>,
}

interface ITileFlexProps extends ITileBaseProps {
  layout?: 'flex',
  templateColumns?: never,
  templateRows?: never,
  gap?: never,
}

interface ITileGridProps extends ITileBaseProps {
  layout: 'grid',
  templateColumns?: TResponsiveValue<string | number>,
  templateRows?: TResponsiveValue<string | number>,
  gap?: TResponsiveValue<number> | TResponsiveValue<string>,
}

type ITileProps = ITileFlexProps | ITileGridProps;

const Tile = ({
  dataTest = 'tile',
  children,
  className,
  layout = 'flex',
  ref,
  ...rest
}: ITileProps): ReactElement => {
  const tileClassName = cn(styles.tile, className);

  if (layout === 'grid') {
    const { templateColumns, templateRows, gap } = rest as Pick<ITileGridProps, 'templateColumns' | 'templateRows' | 'gap'>;
    return (
      <Grid
        templateColumns={ templateColumns }
        templateRows={ templateRows }
        gap={ gap }
        className={ cn(tileClassName, styles.tileGrid) }
        dataTest={ dataTest }
        ref={ ref }
      >
        { children }
      </Grid>
    );
  }

  return (
    <Flex
      direction="column"
      className={ tileClassName }
      dataTest={ dataTest }
      ref={ ref }
    >
      { children }
    </Flex>
  );
};

export { Tile };
export type { ITileProps };
